import React, { useEffect, useState } from 'react';
import { Alert, Box, ButtonBase, IconButton, InputBase, Skeleton, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  HelpArticleSummary,
  HelpCollectionSummary,
  friendlyAssistantError,
  listHelpCollections,
  searchHelpArticles,
} from '../../../services/assistantCentral';
import MessengerHeader from './MessengerHeader';
import { MESSENGER, messengerListRowSx, messengerSearchShellSx, messengerSubtitleSx } from './messengerTheme';

const SEARCH_DEBOUNCE_MS = 300;

export function HelpArticleRow({ article, onOpen, showCollection }: { article: HelpArticleSummary; onOpen: (slug: string) => void; showCollection?: boolean }) {
  return (
    <ButtonBase onClick={() => onOpen(article.slug)} sx={messengerListRowSx}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.86rem', color: MESSENGER.navy, lineHeight: 1.35 }}>{article.title}</Typography>
        {article.excerpt && (
          <Typography
            sx={{
              ...messengerSubtitleSx,
              mt: 0.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {article.excerpt}
          </Typography>
        )}
        {showCollection && (
          <Typography sx={{ fontSize: '0.72rem', color: MESSENGER.slateFaint, mt: 0.4 }}>{article.collection_title}</Typography>
        )}
      </Box>
      <ChevronRightIcon sx={{ fontSize: 20, color: MESSENGER.navy, flexShrink: 0 }} />
    </ButtonBase>
  );
}

interface MessengerHelpProps {
  query: string;
  onQueryChange: (q: string) => void;
  onClose: () => void;
  onOpenCollection: (collection: HelpCollectionSummary) => void;
  onOpenArticle: (slug: string) => void;
  onAskAssistant: (question: string) => void;
}

const MessengerHelp: React.FC<MessengerHelpProps> = ({ query, onQueryChange, onClose, onOpenCollection, onOpenArticle, onAskAssistant }) => {
  const [collections, setCollections] = useState<HelpCollectionSummary[] | null>(null);
  const [results, setResults] = useState<HelpArticleSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const trimmed = query.trim();

  useEffect(() => {
    let alive = true;
    listHelpCollections()
      .then((data) => alive && setCollections(data))
      .catch((e) => {
        if (!alive) return;
        setCollections([]);
        setError(friendlyAssistantError(e, 'Não foi possível carregar a central de ajuda.'));
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!trimmed) {
      setResults(null);
      return undefined;
    }
    let alive = true;
    setResults(null);
    const timer = setTimeout(() => {
      searchHelpArticles(trimmed)
        .then((data) => alive && setResults(data))
        .catch(() => alive && setResults([]));
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [trimmed]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: MESSENGER.surface }}>
      <MessengerHeader title="Ajuda" onClose={onClose}>
        <Box sx={messengerSearchShellSx}>
          <InputBase
            fullWidth
            autoFocus={Boolean(trimmed)}
            value={query}
            onChange={(e) => onQueryChange(e.target.value.slice(0, 200))}
            placeholder="Qual é a sua dúvida?"
            inputProps={{ 'aria-label': 'Pesquisar artigos de ajuda' }}
            sx={{ fontSize: '0.875rem', py: 0.75, color: MESSENGER.navy }}
          />
          {query ? (
            <IconButton size="small" aria-label="Limpar busca" onClick={() => onQueryChange('')} sx={{ color: MESSENGER.slate }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          ) : (
            <SearchIcon sx={{ fontSize: 20, color: MESSENGER.navy }} />
          )}
        </Box>
      </MessengerHeader>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {error && (
          <Alert severity="warning" sx={{ m: 2, fontSize: '0.78rem' }}>
            {error}
          </Alert>
        )}

        {trimmed ? (
          <>
            {results === null ? (
              [0, 1, 2].map((i) => <Skeleton key={i} height={64} sx={{ mx: 2 }} />)
            ) : (
              <>
                <Typography sx={{ px: 2, pt: 2, pb: 1, fontWeight: 700, fontSize: '0.95rem', color: MESSENGER.navy }}>
                  {results.length ? `${results.length} ${results.length === 1 ? 'resultado' : 'resultados'}` : 'Nenhum artigo encontrado'}
                </Typography>
                {results.map((a) => (
                  <HelpArticleRow key={a.slug} article={a} onOpen={onOpenArticle} showCollection />
                ))}
                <Box sx={{ px: 2, py: 2 }}>
                  <ButtonBase
                    onClick={() => onAskAssistant(trimmed)}
                    sx={{
                      width: '100%',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.5,
                      borderRadius: '12px',
                      bgcolor: MESSENGER.tealSoft,
                      color: MESSENGER.tealDark,
                      fontWeight: 700,
                      fontSize: '0.84rem',
                      textAlign: 'left',
                    }}
                  >
                    Perguntar à IA: “{trimmed.length > 48 ? `${trimmed.slice(0, 47)}…` : trimmed}”
                    <ChevronRightIcon sx={{ fontSize: 20 }} />
                  </ButtonBase>
                </Box>
              </>
            )}
          </>
        ) : (
          <>
            <Typography sx={{ px: 2, pt: 2, pb: 1, fontWeight: 700, fontSize: '0.95rem', color: MESSENGER.navy, borderBottom: `1px solid ${MESSENGER.border}` }}>
              {collections === null ? 'Coleções' : `${collections.length} ${collections.length === 1 ? 'coleção' : 'coleções'}`}
            </Typography>
            {collections === null
              ? [0, 1, 2, 3].map((i) => <Skeleton key={i} height={70} sx={{ mx: 2 }} />)
              : collections.map((c) => (
                  <ButtonBase key={c.id} onClick={() => onOpenCollection(c)} sx={messengerListRowSx}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: MESSENGER.navy }}>{c.title}</Typography>
                      <Typography sx={{ ...messengerSubtitleSx, mt: 0.35 }}>{c.description}</Typography>
                      <Typography sx={{ fontSize: '0.76rem', color: MESSENGER.slateFaint, mt: 0.5 }}>
                        {c.article_count} {c.article_count === 1 ? 'artigo' : 'artigos'}
                      </Typography>
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: 20, color: MESSENGER.navy, flexShrink: 0 }} />
                  </ButtonBase>
                ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MessengerHelp;
